import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Link, 
  CheckCircle, 
  XCircle, 
  ArrowsClockwise, 
  Warning,
  Eye,
  EyeSlash,
  CalendarCheck,
  PaperPlaneTilt,
  Info
} from '@phosphor-icons/react';
import api from '@/utils/api';

const MetricoolConnectionSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [formData, setFormData] = useState({
    apiToken: '',
    blogId: '',
    metricoolUserId: ''
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/metricool/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Metricool status:', error);
      setStatus({ connected: false, status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    if (!formData.apiToken || formData.apiToken.length < 10) {
      toast.error('Please enter a valid Metricool API token');
      return;
    }

    setConnecting(true);
    try {
      const response = await api.post('/metricool/connect', {
        api_token: formData.apiToken,
        blog_id: formData.blogId || null,
        metricool_user_id: formData.metricoolUserId || null
      });
      
      if (response.data.success) {
        toast.success('Metricool connected successfully!');
        setShowForm(false);
        setFormData({ apiToken: '', blogId: '', metricoolUserId: '' });
        fetchStatus();
      } else {
        toast.error(response.data.error || 'Failed to connect');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Metricool');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Metricool?')) {
      return;
    }

    setDisconnecting(true);
    try {
      await api.post('/metricool/disconnect');
      toast.success('Metricool disconnected');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center py-8">
          <ArrowsClockwise size={24} className="animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center">
            <CalendarCheck size={24} className="text-white" weight="fill" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Metricool Connection</h2>
            <p className="text-sm text-slate-500">Direct social media scheduling</p>
          </div>
        </div>
        
        {/* Status Badge */}
        {status?.connected ? (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full">
            <CheckCircle size={18} weight="fill" />
            <span className="text-sm font-medium">Connected</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full">
            <XCircle size={18} weight="fill" />
            <span className="text-sm font-medium">Not Connected</span>
          </div>
        )}
      </div>

      {status?.connected ? (
        /* Connected State */
        <div className="space-y-4">
          {/* Connection Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500">Last Sync</div>
              <div className="text-slate-900 font-medium">
                {status.last_sync ? formatDate(status.last_sync) : 'Never'}
              </div>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="text-sm text-emerald-600">Posts Sent</div>
              <div className="text-emerald-700 font-semibold text-xl">
                {status.posts_sent || 0}
              </div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="text-sm text-red-600">Failed</div>
              <div className="text-red-700 font-semibold text-xl">
                {status.failed_posts || 0}
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-600">Connected</div>
              <div className="text-blue-700 font-medium text-sm">
                {status.connected_at ? formatDate(status.connected_at) : 'Unknown'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              data-testid="disconnect-metricool-btn"
              className="px-4 py-2 text-red-600 hover:bg-red-50 font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
            <button
              onClick={() => setShowForm(true)}
              data-testid="reconnect-metricool-btn"
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
            >
              Reconnect
            </button>
          </div>
        </div>
      ) : (
        /* Disconnected State */
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Info size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Connect your Metricool account</p>
                <p>
                  Connecting allows PayGBot to push your generated content directly to 
                  Metricool's planner. No more manual exports!
                </p>
                <p className="mt-2 text-amber-700">
                  <strong>Note:</strong> Metricool API access requires an Advanced or Custom plan.
                </p>
              </div>
            </div>
          </div>

          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              data-testid="connect-metricool-btn"
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
            >
              <Link size={20} weight="bold" />
              <span>Connect Metricool</span>
            </button>
          ) : null}
        </div>
      )}

      {/* Connection Form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleConnect}
            className="mt-6 pt-6 border-t border-slate-200 space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Metricool API Token
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={formData.apiToken}
                  onChange={(e) => setFormData(prev => ({ ...prev, apiToken: e.target.value }))}
                  required
                  data-testid="metricool-api-token-input"
                  placeholder="Enter your Metricool API token"
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showToken ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Find this in Metricool: Settings &gt; API &gt; Generate Token
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Blog/Brand ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.blogId}
                onChange={(e) => setFormData(prev => ({ ...prev, blogId: e.target.value }))}
                data-testid="metricool-blog-id-input"
                placeholder="e.g., 6024870"
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              />
              <p className="mt-1 text-xs text-slate-500">
                Find in your Metricool URL: <code className="bg-slate-100 px-1 rounded">blogId=XXXXXX</code>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                User ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.metricoolUserId}
                onChange={(e) => setFormData(prev => ({ ...prev, metricoolUserId: e.target.value }))}
                data-testid="metricool-user-id-input"
                placeholder="e.g., 4620087"
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              />
              <p className="mt-1 text-xs text-slate-500">
                Find in your Metricool URL: <code className="bg-slate-100 px-1 rounded">userId=XXXXXX</code> (often different from Blog ID)
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ apiToken: '', blogId: '', metricoolUserId: '' });
                }}
                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={connecting || !formData.apiToken || !formData.blogId || !formData.metricoolUserId}
                data-testid="submit-metricool-connection-btn"
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {connecting ? (
                  <>
                    <ArrowsClockwise size={20} className="animate-spin" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <PaperPlaneTilt size={20} weight="fill" />
                    <span>Connect</span>
                  </>
                )}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MetricoolConnectionSection;
